import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Lock } from "lucide-react";
import { authApi } from "../services/http";
import { toast } from "../components/Toast";
import ThemeToggle from "../components/ThemeToggle";
import "./Login.css";

const schema = z
  .object({
    password: z
      .string()
      .min(8, "A palavra-passe deve ter pelo menos 8 caracteres.")
      .max(200, "A palavra-passe é demasiado longa."),
    confirm: z.string().min(1, "Repita a palavra-passe."),
  })
  .refine((data) => data.password === data.confirm, {
    path: ["confirm"],
    message: "As palavras-passe não coincidem.",
  });

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  // Convite de um utilizador novo: mesma ligação, textos de boas-vindas.
  const isInvite = searchParams.get("convite") === "1";
  const navigate = useNavigate();

  // "checking" | "valid" | "invalid"
  const [status, setStatus] = useState(token ? "checking" : "invalid");
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  useEffect(() => {
    let active = true;
    if (!token) return undefined;
    (async () => {
      try {
        const { data } = await authApi.get("/auth/reset-password", { params: { token } });
        if (active) setStatus(data?.valid ? "valid" : "invalid");
      } catch {
        if (active) setStatus("invalid");
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  async function onSubmit(data) {
    setServerError("");
    try {
      await authApi.post("/auth/reset-password", { token, password: data.password });
      toast(isInvite ? "Palavra-passe criada. Já pode entrar." : "Palavra-passe atualizada. Inicie sessão.", "success");
      navigate("/login");
    } catch (err) {
      if (err.response?.status === 400) {
        setStatus("invalid");
      } else if (err.response?.status === 429) {
        setServerError(
          err.response?.data?.message || "Muitos pedidos. Tente novamente dentro de alguns minutos."
        );
      } else {
        setServerError(
          err.response?.data?.message || "Não foi possível atualizar a palavra-passe. Tente novamente."
        );
      }
    }
  }

  return (
    <div className="login-bg">
      <div className="login-shell" role="main">
        <div className="login-hero" aria-label="Apresentação do painel da igreja">
          <div className="hero-badge">PAINEL DA IGREJA</div>
          <h2>{isInvite ? "Bem-vindo(a) ao painel" : "Definir nova palavra-passe"}</h2>
          <p>
            {isInvite
              ? "Crie a palavra-passe da sua conta. Depois, entre com o seu e-mail e esta palavra-passe."
              : "Escolha uma palavra-passe forte e exclusiva. Ao concluir, todas as sessões abertas nesta conta serão terminadas por segurança."}
          </p>
        </div>

        <div className="login-card glass" aria-label="Formulário de nova palavra-passe">
          <div className="login-row" style={{ alignItems: "center" }}>
            <div className="login-brand" aria-label="Painel da igreja">
              <span className="dot" />
              Painel da igreja
            </div>
            <ThemeToggle />
          </div>

          <div className="login-lock" aria-hidden="true">
            <KeyRound size={28} strokeWidth={2.25} />
          </div>
          <div className="login-title">{isInvite ? "Criar palavra-passe" : "Nova palavra-passe"}</div>
          <div className="login-subtitle">
            {isInvite
              ? "Escolha a palavra-passe que vai usar para entrar no painel."
              : "Introduza e confirme a nova palavra-passe da sua conta."}
          </div>

          {status === "checking" && (
            <div className="login-subtitle" style={{ marginTop: 16 }}>
              A validar a ligação…
            </div>
          )}

          {status === "invalid" && (
            <>
              <div className="alert-error">
                {isInvite
                  ? "Este convite é inválido ou expirou. Peça um novo ao administrador da sua igreja."
                  : "Esta ligação de recuperação é inválida ou expirou. Peça uma nova."}
              </div>
              {!isInvite && (
                <div className="login-footer">
                  <Link className="link-muted" to="/recuperar-palavra-passe">
                    Pedir nova ligação de recuperação
                  </Link>
                </div>
              )}
            </>
          )}

          {status === "valid" && (
            <>
              {serverError && <div className="alert-error">{serverError}</div>}
              <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                <label className="label" htmlFor="password">
                  Nova palavra-passe
                </label>
                <div className="input-group">
                  <span className="input-icon" aria-hidden="true">
                    <Lock size={18} strokeWidth={2} />
                  </span>
                  <input
                    id="password"
                    className={`input ${errors.password ? "has-error" : ""}`}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    autoFocus
                    aria-label="Nova palavra-passe"
                    aria-invalid={Boolean(errors.password)}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  >
                    {showPassword ? "Ocultar" : "Mostrar"}
                  </button>
                </div>
                {errors.password && <div className="field-hint">{errors.password.message}</div>}

                <label className="label" htmlFor="confirm">
                  Confirmar palavra-passe
                </label>
                <div className="input-group">
                  <span className="input-icon" aria-hidden="true">
                    <Lock size={18} strokeWidth={2} />
                  </span>
                  <input
                    id="confirm"
                    className={`input ${errors.confirm ? "has-error" : ""}`}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    aria-label="Confirmar palavra-passe"
                    aria-invalid={Boolean(errors.confirm)}
                    {...register("confirm")}
                  />
                </div>
                {errors.confirm && <div className="field-hint">{errors.confirm.message}</div>}

                <button
                  className="btn-primary btn-gradient"
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting ? "A guardar..." : isInvite ? "Criar palavra-passe" : "Guardar nova palavra-passe"}
                </button>
              </form>
            </>
          )}

          <div className="login-footer">
            <Link className="link-muted" to="/login">
              Voltar ao início de sessão
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
